import { Controller } from "@hotwired/stimulus"

/**
 * Activity Hours Controller
 * Calculates activity duration: (to - from - break) × employee_count
 * Updates individual row hours and total hours in real-time
 */
export default class extends Controller {
  static targets = ["entries", "row", "total"]

  connect() {
    // Calculate on initial load (with small delay to ensure DOM is ready)
    requestAnimationFrame(() => this.calculate())

    // Also listen for changes on the container (for SlimSelect which may bubble differently)
    this.element.addEventListener("change", this.handleChange.bind(this))

    // Observe DOM for new rows being added (e.g., via nested-form controller)
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
          // Nodes added or removed, recalculate
          requestAnimationFrame(() => this.calculate())
          break
        }
      }
    })

    // Start observing the entries container for child additions
    const entriesContainer = this.hasEntriesTarget ? this.entriesTarget : this.element
    this.observer.observe(entriesContainer, { childList: true, subtree: true })
  }

  disconnect() {
    this.element.removeEventListener("change", this.handleChange.bind(this))
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  handleChange(_event) {
    // Recalculate when any input changes
    this.calculate()
  }

  calculate() {
    let totalHours = 0

    // Find all rows (both div and tr based layouts)
    const rows = this.element.querySelectorAll('.nested-fields:not([style*="display: none"])')

    rows.forEach((row) => {
      // Skip rows marked for destruction
      const destroyField = row.querySelector('input[name*="_destroy"]')
      if (destroyField && (destroyField.value === "true" || destroyField.value === "1")) {
        return
      }

      const fromInput = row.querySelector('[data-activity-hours-target="from"]')
      const toInput = row.querySelector('[data-activity-hours-target="to"]')
      const breakInput = row.querySelector('[data-activity-hours-target="break"]')
      const hoursInput = row.querySelector('[data-activity-hours-target="hours"]')
      const employeeSelect = row.querySelector('[data-activity-hours-target="employeeIds"]')
      const multiplyHoursInput = row.querySelector('[data-activity-hours-target="multiplyHours"]')

      if (!fromInput || !toInput || !hoursInput) return

      const fromTime = this.parseTime(fromInput.value)
      const toTime = this.parseTime(toInput.value)
      const breakTime = breakInput ? this.parseTime(breakInput.value) : 0

      if (fromTime === null || toTime === null) {
        hoursInput.value = ""
        return
      }

      // Calculate duration in hours
      let duration = (toTime - fromTime) / 60 // Convert minutes to hours
      if (duration < 0) duration += 24 // Handle overnight shifts

      // Subtract break time
      if (breakTime !== null && breakTime > 0) {
        duration -= breakTime / 60
      }

      // Multiply by employee count if setting is enabled
      const shouldMultiply = multiplyHoursInput && multiplyHoursInput.value === "1"
      if (shouldMultiply && employeeSelect) {
        // Get selected values - works for both native select and SlimSelect
        let employeeCount
        if (employeeSelect.selectedOptions) {
          employeeCount = Array.from(employeeSelect.selectedOptions).filter(
            (opt) => opt.value !== "",
          ).length
        } else {
          employeeCount = Array.from(employeeSelect.querySelectorAll("option:checked")).filter(
            (opt) => opt.value !== "",
          ).length
        }
        if (employeeCount > 0) {
          duration *= employeeCount
        }
      }

      // Round to 2 decimal places
      duration = Math.round(duration * 100) / 100

      // Update the hours field
      if (duration > 0) {
        hoursInput.value = duration.toFixed(2)
        totalHours += duration
      } else {
        hoursInput.value = ""
      }
    })

    // Update total if target exists
    if (this.hasTotalTarget) {
      this.totalTarget.textContent = totalHours.toFixed(2)
    }
  }

  /**
   * Parse time string (HH:MM) to minutes since midnight
   */
  parseTime(timeString) {
    if (!timeString) return null

    const parts = timeString.split(":")
    if (parts.length < 2) return null

    const hours = parseInt(parts[0], 10)
    const minutes = parseInt(parts[1], 10)

    if (isNaN(hours) || isNaN(minutes)) return null

    return hours * 60 + minutes
  }
}
